import config from "appConfig";
import { getLinkForCourseLesson } from "../../../../services/Utils";

import lesson0207 from "../lesson-07/lesson";

const linkLesson0207 = getLinkForCourseLesson("4eb31243-b6cb-423f-919d-15bba103f7ce", lesson0207.id);

const theory = `<p>
Sound signals whose spectral components are mainly located in the high frequency range are perceived by humans as “sharp” or “shrill”. The further the peak of a spectrum’s envelope or spectral “center of mass” is shifted towards high frequencies, the higher is the perceived sharpness of the corresponding sound  [6]. This means that the shape of the spectrum is decisive in the estimation of psychoacoustic sharpness, as demonstrated in <a href=# data-img-src="${config.ASSETS_URL}/02/10/theory/figure-1.svg" data-img-alt="Figure 1. Comparison of two sounds with similar SPL but different spectral shapes and their calculated psychoacoustic sharpness values.">Figure 1</a>. In other words, sharpness is a measure for the high frequency content of a sound.
<figure class=figure>
<p><img src="${config.ASSETS_URL}/02/10/theory/figure-1.svg" alt="Figure 1. Comparison of two sounds with similar SPL but different spectral shapes and their calculated psychoacoustic sharpness values.">
<figcaption class=caption>Figure 1. Comparison of two sounds with similar SPL but different spectral shapes and their calculated psychoacoustic sharpness values.</figcaption></figure>
<p>
The unit for sharpness is acum, which in Greek means sharp. One acum is defined as the sharpness of a narrow-band noise with a bandwidth smaller than 160 Hz, a centre frequency of
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msub>
      <mrow>
         <mi>f</mi>
      </mrow>
      <mrow>
         <mi>c</mi>
      </mrow>
   </msub>
   <mo class=MathClass-rel>=</mo>
   <mstyle class=text>
      <mtext>1</mtext>
   </mstyle>
   <mstyle class=text>
      <mtext>kHz</mtext>
   </mstyle>
</math>
, and an SPL of 60 dB [6]. Psychoacoustic sharpness (
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>S</mi>
</math>
) is generally calculated by weighting and integrating <span class=cmti-10>specific loudness</span> (
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msup>
      <mrow>
         <mi>N</mi>
      </mrow>
      <mrow>
         <mi>&prime;</mi>
      </mrow>
   </msup>
</math>
) values (i.e. the measured loudness within each critical band as introduced in <a href="${linkLesson0207}" target="_blank">Lesson 2.7</a>) and dividing the output by the total loudness as [6]
<table class=equation>
<tr>
   <td>
      <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
         <mi>S</mi>
         <mo class=MathClass-rel>=</mo>
         <mn>0</mn>
         <mo class=MathClass-punc>.</mo>
         <mn>11</mn>
         <mfrac>
            <mrow>
               <msubsup>
                  <mrow>
                     <mo>∫</mo>
                  </mrow>
                  <mrow>
                     <mn>0</mn>
                  </mrow>
                  <mrow>
                     <mn>24</mn>
                     <mstyle class=text>
                        <mtext>Bark</mtext>
                     </mstyle>
                  </mrow>
               </msubsup>
               <msup>
                  <mrow>
                     <mi>N</mi>
                  </mrow>
                  <mrow>
                     <mi>&prime;</mi>
                  </mrow>
               </msup>
               <mi>g</mi>
               <mo stretchy=false>(</mo>
               <mi>z</mi>
               <mo stretchy=false>)</mo>
               <mi>z</mi>
               <mi mathvariant=italic>dz</mi>
            </mrow>
            <mrow>
               <msubsup>
                  <mrow>
                     <mo>∫</mo>
                  </mrow>
                  <mrow>
                     <mn>0</mn>
                  </mrow>
                  <mrow>
                     <mn>24</mn>
                     <mstyle class=text>
                        <mtext>Bark</mtext>
                     </mstyle>
                  </mrow>
               </msubsup>
               <msup>
                  <mrow>
                     <mi>N</mi>
                  </mrow>
                  <mrow>
                     <mi>&prime;</mi>
                  </mrow>
               </msup>
               <mi mathvariant=italic>dz</mi>
            </mrow>
         </mfrac>
         <mstyle class=text>
            <mtext>acum</mtext>
         </mstyle>
         
      </math>
   <td class=eq-no>(1)
</table>
<p>
where
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <msup>
      <mrow>
         <mi>N</mi>
      </mrow>
      <mrow>
         <mi>′</mi>
      </mrow>
   </msup>
</math>
denotes the specific loudness and
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>g</mi>
   <mo class=MathClass-open>(</mo>
   <mi>z</mi>
   <mo class=MathClass-close>)</mo>
</math>
is a standard weighting function which can be calculated as [6]
<table class=equation>
<tr>
   <td style="font-size: 11px;">
      <math class=equation xmlns=http://www.w3.org/1998/Math/MathML display=block>
         <mi>g</mi>
         <mo stretchy=false>(</mo>
         <mi>z</mi>
         <mo stretchy=false>)</mo>
         <mo>=</mo>
         <mo>{</mo>
         <mfrac linethickness="0" numalign="left">
            <mrow>
               <mn>1</mn>
               <mo>,</mo>
            </mrow>
            <mrow>
               <mn>0.15</mn>
               <msup>
                  <mrow>
                     <mi>e</mi>
                  </mrow>
                  <mrow>
                     <mn>0.42</mn>
                     <mo>(</mo>
                     <mfrac>
                        <mrow>
                           <mi>z</mi>
                        </mrow>
                        <mrow>
                           <mtext>Bark</mtext>
                        </mrow>
                     </mfrac>
                     <mo>-</mo>
                     <mn>15.8</mn>
                     <mo>)</mo>
                  </mrow>
               </msup>
               <mo>+</mo>
               <mn>0.85</mn>
               <mo>,</mo>
            </mrow>
         </mfrac>
         <mfrac linethickness="0">
            <mrow>
               <mi>z</mi>
               <mo>&le;</mo>
               <mn>15.8</mn>
               <mo>&nbsp;</mo>
               <mtext> Bark</mtext>
            </mrow>
            <mrow>
               <mi>z</mi>
               <mo>&gt;</mo>
               <mn>15.8</mn>
               <mo>&nbsp;</mo>
               <mtext> Bark</mtext>
            </mrow>
         </mfrac>
         
      </math>
   <td class=eq-no>(2)
</tr>
</table>
<p>Three common methods which use different weighting functions for the calculation of sharpness are:
<p><b>von Bismarck</b> which was developed by von Bismarck [4] and is based on the distribution of the specific loudness throughout the critical band rate.
<p><b>DIN 45692</b> which is based on research by Widmann [5] who performed listening tests and fine tuned the weighting functions used by von Bismarck [2] based on experimental data.
<p>
<b>Aures</b>
which takes the absolute loudness value (
<math display=inline xmlns=http://www.w3.org/1998/Math/MathML>
   <mi>N</mi>
</math>
) into account when calculating sharpness [1], in contrast to the other two methods.
</dl>
<p>
<a href="#" data-img-src="${config.ASSETS_URL}/02/10/theory/figure-2.svg" data-img-alt="Figure 2. Sharpness values for the same noise sample calculated using three different methods.">Figure 2</a> shows the difference in the calculated sharpness values using these three methods. It is interesting to note that Aures’ method produces a different result because, as mentioned before, it takes absolute loudness into consideration while calculating the weighting function [3].<br class=newline>
<figure class=figure>
<p><img src="${config.ASSETS_URL}/02/10/theory/figure-2.svg" alt="Figure 2. Sharpness values for the same noise sample calculated using three different methods."><figcaption class=caption>Figure 2. Sharpness values for the same noise sample calculated using three different methods.</figcaption></figure>
<p>Studies have shown that the spectral envelope plays a more significant role in sharpness perception than spectral fine structures [6]. For an increase from 30 dB to 90 dB SPL, sharpness increases by a factor of two. Thus, according to Fastl [6], SPL may be left out in the first approximation of sharpness.
<p>The factors with the most influence on sharpness are the shape of the spectral envelope, the spectral content (bandwidth), and the centre frequency for narrow band sounds.
<p>
Experiments have shown that sensory pleasantness decreases with increasing sharpness (for constant loudness). This is illustrated in <a href="#" data-img-src="${config.ASSETS_URL}/02/10/theory/figure-3.svg" data-img-alt="Figure 3. Relationship between pleasantness and sharpness. An increase in sharpness leads to a reduction in pleasantness [6].">Figure 3</a> [6]. This highlights the importance of sharpness when determining product sound quality. Examples where sharpness can contribute significantly to the overall impression are household appliances such as vacuum cleaners and hair dryers but also certain engine noises.
<figure class=figure>
<p><img src="${config.ASSETS_URL}/02/10/theory/figure-3.svg" alt="Figure 3. Relationship between pleasantness and sharpness. An increase in sharpness leads to a reduction in pleasantness [6]."><figcaption class=caption>Figure 3. Relationship between pleasantness and sharpness. An increase in sharpness leads to a reduction in pleasantness [6].</figcaption></figure>
<p>
<h3 class=likesectionHead>Glossary</h3>
<dl>
<dt>SPL
<dd>Sound Pressure Level
</dl>
<h3 class=sectionHead>References</h3>
<dl class=thebibliography>
<dt class=thebibliography id=X0-aures1985>[1]
<dd class=thebibliography id=bib-1>
   <p>W Aures. “Berechnungsverfahren Für Den Sensorischen Wohlklang Beliebiger Schallsignale”. In: <span class=cmti-10>Acta Acustica united with Acustica </span>59.2 (1985).
<dt class=thebibliography id=X0-din2009>[2]
<dd class=thebibliography id=bib-2>
   <p><span class=cmti-10>DIN 45692:2009-08 Measurement Technique for the Simulation of </span><span class=cmti-10>the Auditory Sensation of Sharpness</span>. Beuth Verlag GmbH. DOI: <a href="https://doi.org/10.31030/1521326" target="_blank">10.31030/1521326</a>.
<dt class=thebibliography id=X0-swift2017>[3]
<dd class=thebibliography id=bib-3>
   <p>S. Hales Swift and Kent L. Gee. “Implementing Sharpness Using Specific Loudness Calculated from the “Procedure for the Computation of Loudness of Steady Sounds””. In: 173rd Meeting of Acoustical Society of America and 8th Forum Acusticum. Boston, Massachusetts, 2017, p. 030001. DOI: <a href="https://doi.org/10.1121/2.0000542" target="_blank">10.1121/2.0000542</a>.
<dt class=thebibliography id=X0-vonbismarck1974>[4]
<dd class=thebibliography id=bib-4>
   <p>G. von Bismarck. “Sharpness as an Attribute of the Timbre of Steady Sounds”. In: <span class=cmti-10>Acta Acustica united with Acustica </span>30.3 (1974).
<dt class=thebibliography id=X0-widmann1993>[5]
<dd class=thebibliography id=bib-5>
   <p>U Widmann. “Untersuchung Zur Schärfe Und Zur Lästigkeit von Rauschen Unterschiedlicher Spektralverteilung”. In: DAGA. Bad Honnef, 1993.
<dt class=thebibliography id=X0-zwicker2010>[6]
<dd class=thebibliography id=bib-6>
   <p>Eberhard Zwicker and Hugo Fastl. <span class=cmti-10>Psychoacoustics: Facts and Models</span>. 3. ed., [repr.] Springer Series in Information Sciences 22. Berlin Heidelberg: Springer, 2010. <span class=cmcsc-10><span class=small-caps>isbn</span></span>: 978-3-540-23159-2.
</dl>`;

export default theory;
